import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, Navigate, Link } from "react-router-dom"
import {
    Eye,
    EyeOff,
    User,
    Mail,
    Phone,
    Lock,
    Loader2,
    ShieldCheck,
    Store,
} from "lucide-react"

import { useAuth } from "@/hooks/useAuth"
import { showSuccess, showError } from "@/utils/toast"
import { isAuthenticated } from "@/utils/authStorage"
import type { RegisterPayload } from "@/types/auth.types"
import Logo from "@/assets/logo.png"

type RegisterStep = "form" | "otp" | "done"

const OTP_RESEND_SECONDS = 60
const OTP_LENGTH = 6

const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

const validateVietnamPhone = (value: string) => {
    const cleaned = value.replace(/\s+/g, "")
    return /^(0|\+84)(3|5|7|8|9)\d{8}$/.test(cleaned)
}

const validateStrongPassword = (value: string) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.$!%*?&])[A-Za-z\d@.$!%*?&]{8,}$/.test(value)
}

const getPasswordError = (value: string) => {
    if (!value) return "Vui lòng nhập mật khẩu"
    if (value.length < 8) return "Mật khẩu phải có ít nhất 8 ký tự"
    if (!/[A-Z]/.test(value)) return "Mật khẩu phải có ít nhất 1 chữ hoa"
    if (!/[a-z]/.test(value)) return "Mật khẩu phải có ít nhất 1 chữ thường"
    if (!/\d/.test(value)) return "Mật khẩu phải có ít nhất 1 số"
    if (!/[@.$!%*?&]/.test(value)) {
        return "Mật khẩu phải có ít nhất 1 ký hiệu đặc biệt: @ . $ ! % * ? &"
    }
    if (/[^A-Za-z\d@.$!%*?&]/.test(value)) {
        return "Mật khẩu chỉ được chứa ký tự đặc biệt thuộc nhóm: @ . $ ! % * ? &"
    }
    return ""
}

const Register = () => {
    const navigate = useNavigate()
    const { register, verifyOtp, resendOtp, loading } = useAuth()

    const [step, setStep] = useState<RegisterStep>("form")

    const [fullName, setFullName] = useState("")
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""))
    const [otpCountdown, setOtpCountdown] = useState(OTP_RESEND_SECONDS)
    const otpRefs = useRef<Array<HTMLInputElement | null>>([])

    const otp = otpDigits.join("")

    useEffect(() => {
        if (step !== "otp") return
        if (otpCountdown <= 0) return

        const timer = window.setInterval(() => {
            setOtpCountdown((prev) => {
                if (prev <= 1) {
                    window.clearInterval(timer)
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => window.clearInterval(timer)
    }, [step, otpCountdown])

    useEffect(() => {
        if (step === "otp") {
            window.setTimeout(() => otpRefs.current[0]?.focus(), 100)
        }
    }, [step])

    const passwordError = useMemo(() => getPasswordError(password), [password])

    const confirmPasswordError = useMemo(() => {
        if (!confirmPassword) return "Vui lòng nhập lại mật khẩu"
        if (confirmPassword !== password) return "Mật khẩu xác nhận không khớp"
        return ""
    }, [confirmPassword, password])

    const fieldErrors = useMemo(() => {
        return {
            fullName: !fullName.trim() ? "Vui lòng nhập họ và tên" : "",
            email:
                !email.trim()
                    ? "Vui lòng nhập email"
                    : !validateEmail(email.trim())
                        ? "Email không đúng định dạng"
                        : "",
            phone:
                !phone.trim()
                    ? "Vui lòng nhập số điện thoại"
                    : !validateVietnamPhone(phone.trim())
                        ? "Số điện thoại Việt Nam không hợp lệ"
                        : "",
            password: password ? passwordError : "",
            confirmPassword: confirmPassword ? confirmPasswordError : "",
        }
    }, [fullName, email, phone, password, passwordError, confirmPassword, confirmPasswordError])

    const canSubmit = useMemo(() => {
        return Boolean(
            fullName.trim() &&
            email.trim() &&
            phone.trim() &&
            password.trim() &&
            confirmPassword.trim() &&
            validateEmail(email.trim()) &&
            validateVietnamPhone(phone.trim()) &&
            validateStrongPassword(password) &&
            password === confirmPassword
        )
    }, [fullName, email, phone, password, confirmPassword])

    if (isAuthenticated()) {
        return <Navigate to="/" replace />
    }

    const buildPayload = (): RegisterPayload => {
        return {
            fullName: fullName.trim(),
            email: email.trim(),
            phone: phone.trim(),
            password,
            registrationType: "Vendor",
        }
    }

    const resetOtpInputs = () => {
        setOtpDigits(Array(OTP_LENGTH).fill(""))
        window.setTimeout(() => otpRefs.current[0]?.focus(), 100)
    }

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (loading) return

        if (!canSubmit) {
            showError("Vui lòng kiểm tra lại thông tin đăng ký")
            return
        }

        try {
            const payload = buildPayload()
            const res = await register(payload)

            showSuccess(res.message || "Vui lòng kiểm tra email để xác minh tài khoản")
            resetOtpInputs()
            setOtpCountdown(OTP_RESEND_SECONDS)
            setStep("otp")
        } catch (err: any) {
            console.error("register error", err?.response?.data || err)
            showError(
                err?.response?.data?.message ||
                err?.response?.data?.errors?.[0] ||
                err?.message ||
                "Đăng ký không thành công"
            )
        }
    }

    const onVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault()
        if (loading) return

        if (otp.length !== OTP_LENGTH) {
            showError("Vui lòng nhập đầy đủ 6 số OTP")
            return
        }

        try {
            await verifyOtp({
                email: email.trim(),
                otpCode: otp,
            })

            showSuccess("Xác minh tài khoản thành công.")
            setStep("done")
        } catch (err: any) {
            console.error("verify otp error", err?.response?.data || err)
            showError(
                err?.response?.data?.message ||
                err?.response?.data?.errors?.[0] ||
                err?.message ||
                "Xác minh OTP không thành công"
            )
        }
    }

    const onResendOtp = async () => {
        if (loading || otpCountdown > 0) return

        try {
            const res = await resendOtp({ email: email.trim() })
            showSuccess(res.message || "Đã gửi lại mã OTP")
            resetOtpInputs()
            setOtpCountdown(OTP_RESEND_SECONDS)
        } catch (err: any) {
            console.error("resend otp error", err?.response?.data || err)
            showError(
                err?.response?.data?.message ||
                err?.response?.data?.errors?.[0] ||
                err?.message ||
                "Gửi lại OTP không thành công"
            )
        }
    }

    const handleOtpChange = (index: number, value: string) => {
        const digit = value.replace(/\D/g, "").slice(-1)
        const next = [...otpDigits]
        next[index] = digit
        setOtpDigits(next)

        if (digit && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus()
        }
    }

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace") {
            if (otpDigits[index]) {
                const next = [...otpDigits]
                next[index] = ""
                setOtpDigits(next)
                return
            }

            if (index > 0) {
                otpRefs.current[index - 1]?.focus()
                const next = [...otpDigits]
                next[index - 1] = ""
                setOtpDigits(next)
            }
        }

        if (e.key === "ArrowLeft" && index > 0) {
            otpRefs.current[index - 1]?.focus()
        }

        if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus()
        }
    }

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH)
        if (!pasted) return

        const next = Array(OTP_LENGTH).fill("")
        pasted.split("").forEach((char, idx) => {
            next[idx] = char
        })

        setOtpDigits(next)

        const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
        otpRefs.current[focusIndex]?.focus()
    }

    const formatCountdown = (seconds: number) => {
        const minutes = Math.floor(seconds / 60)
        const remainSeconds = seconds % 60
        return `${String(minutes).padStart(2, "0")}:${String(remainSeconds).padStart(2, "0")}`
    }

    return (
        <div className="eco-animated-bg relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-10">
            <div className="bg-glow" />

            <div className="relative z-10 w-full max-w-2xl space-y-6 rounded-2xl border border-white/40 bg-white/80 p-8 shadow-2xl backdrop-blur-xl">
                <div className="text-center space-y-2">
                    <Link to="/" className="group inline-flex flex-col items-center">
                        <div className="mx-auto h-20 w-28 overflow-hidden">
                            <img
                                src={Logo}
                                alt="CloseExp AI"
                                className="h-full w-full object-cover object-top transition-all duration-500 group-hover:scale-105 group-hover:drop-shadow-[0_0_24px_rgba(16,185,129,0.4)]"
                            />
                        </div>
                    </Link>

                    <h1 className="pt-1 text-2xl font-bold text-gray-800">
                        {step === "form" && "Tạo tài khoản"}
                        {step === "otp" && "Xác minh OTP"}
                        {step === "done" && "Đăng ký thành công"}
                    </h1>

                    <p className="text-sm text-gray-500">
                        {step === "form" && "Điền thông tin để tạo tài khoản khách hàng"}
                        {step === "otp" && "Nhập mã OTP đã được gửi về email của bạn"}
                    </p>
                </div>

                {step === "form" && (
                    <form onSubmit={onSubmit} className="space-y-6">
                        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white p-4">
                            <div className="flex items-start gap-3">
                                <div className="rounded-xl bg-emerald-100 p-2 text-emerald-700">
                                    <Store size={18} />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-800">Tài khoản khách hàng</p>
                                    <p className="mt-1 text-sm text-gray-600">
                                        Sau khi đăng ký tài khoản, bạn cần xác minh OTP qua email để kích hoạt.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-gray-100 bg-white/70 p-5 space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-base font-semibold text-gray-800">
                                        Thông tin tài khoản
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        Vui lòng điền các thông tin cơ bản để tạo tài khoản
                                    </p>
                                </div>

                                <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                                    Khách hàng
                                </span>
                            </div>

                            <div className="flex flex-col gap-4">
                                <Input
                                    icon={<User size={16} />}
                                    label="Họ và tên"
                                    value={fullName}
                                    setValue={setFullName}
                                    error={fieldErrors.fullName}
                                />
                                <Input
                                    icon={<Mail size={16} />}
                                    label="Email"
                                    type="email"
                                    value={email}
                                    setValue={setEmail}
                                    error={fieldErrors.email}
                                />
                                <Input
                                    icon={<Phone size={16} />}
                                    label="Số điện thoại"
                                    value={phone}
                                    setValue={setPhone}
                                    error={fieldErrors.phone}
                                />
                                <PasswordInput
                                    label="Mật khẩu"
                                    value={password}
                                    setValue={setPassword}
                                    show={showPassword}
                                    setShow={setShowPassword}
                                    error={fieldErrors.password}
                                    hint="Ít nhất 8 ký tự, gồm chữ hoa, chữ thường, số và 1 ký hiệu trong nhóm @ . $ ! % * ? &"
                                />
                                <PasswordInput
                                    label="Xác nhận mật khẩu"
                                    value={confirmPassword}
                                    setValue={setConfirmPassword}
                                    show={showConfirmPassword}
                                    setShow={setShowConfirmPassword}
                                    error={fieldErrors.confirmPassword}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !canSubmit}
                            className="w-full rounded-lg bg-gradient-to-r from-green-400 to-emerald-500 py-2.5 font-semibold text-white disabled:opacity-60"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="animate-spin" size={18} />
                                    Đang xử lý...
                                </span>
                            ) : (
                                "Đăng ký"
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="w-full rounded-lg border border-gray-200 py-2.5 font-medium text-gray-600 hover:bg-gray-50"
                        >
                            Quay lại đăng nhập
                        </button>
                    </form>
                )}

                {step === "otp" && (
                    <form onSubmit={onVerifyOtp} className="space-y-6">
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-5">
                            <div className="flex items-start gap-3">
                                <ShieldCheck className="mt-0.5 text-emerald-600" size={20} />
                                <div>
                                    <p className="font-semibold text-emerald-800">Kiểm tra email của bạn</p>
                                    <p className="mt-1 text-sm text-emerald-700">
                                        Mã OTP đang được gửi đến{" "}
                                        <span className="font-medium">{email}</span>. Email có thể
                                        đến chậm 1–2 phút — kiểm tra cả hộp thư spam. Nếu chưa nhận
                                        được, dùng nút gửi lại sau khi hết đếm ngược.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-600">Mã OTP</label>
                            <div className="flex items-center justify-center gap-2 sm:gap-3">
                                {otpDigits.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => {
                                            otpRefs.current[index] = el
                                        }}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onPaste={handleOtpPaste}
                                        className="h-14 w-12 rounded-2xl border border-gray-200 bg-white text-center text-xl font-bold text-gray-800 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 sm:h-16 sm:w-14"
                                    />
                                ))}
                            </div>
                            <p className="text-center text-xs text-gray-500">
                                Vui lòng nhập đủ 6 chữ số được gửi về email
                            </p>
                        </div>

                        <p className="text-sm text-left text-gray-500">
                            {otpCountdown > 0 ? (
                                <>
                                    Bạn có thể gửi lại mã sau{" "}
                                    <span className="font-semibold text-gray-700">
                                        {formatCountdown(otpCountdown)}
                                    </span>
                                </>
                            ) : (
                                <>Bạn đã có thể gửi lại mã OTP</>
                            )}
                        </p>

                        <button
                            type="submit"
                            disabled={loading || otp.length !== OTP_LENGTH}
                            className="w-full rounded-lg bg-gradient-to-r from-green-400 to-emerald-500 py-2.5 font-semibold text-white disabled:opacity-60"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="animate-spin" size={18} />
                                    Đang xác minh...
                                </span>
                            ) : (
                                "Xác minh OTP"
                            )}
                        </button>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <button
                                type="button"
                                onClick={onResendOtp}
                                disabled={loading || otpCountdown > 0}
                                className="w-full rounded-lg border border-emerald-200 py-2.5 font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                            >
                                Gửi lại mã OTP
                            </button>

                            <button
                                type="button"
                                onClick={() => setStep("form")}
                                disabled={loading}
                                className="w-full rounded-lg border border-gray-200 py-2.5 font-medium text-gray-600 hover:bg-gray-50"
                            >
                                Quay lại chỉnh thông tin
                            </button>
                        </div>
                    </form>
                )}

                {step === "done" && (
                    <div className="space-y-6">
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-6 text-center">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                                <ShieldCheck className="text-emerald-600" size={24} />
                            </div>

                            <p className="mt-2 text-sm text-gray-600">
                                Email của bạn đã được xác minh. Bây giờ bạn có thể đăng nhập vào hệ
                                thống.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="w-full rounded-lg bg-gradient-to-r from-green-400 to-emerald-500 py-2.5 font-semibold text-white"
                        >
                            Đi đến đăng nhập
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

type InputProps = {
    icon?: React.ReactNode
    label: string
    type?: string
    value: string
    setValue: (value: string) => void
    error?: string
    hint?: string
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"]
    maxLength?: number
}

const Input = ({
    icon,
    label,
    type = "text",
    value,
    setValue,
    error,
    hint,
    inputMode,
    maxLength,
}: InputProps) => (
    <div>
        <label className="text-sm font-medium text-gray-600">{label}</label>
        <div className="relative">
            {icon && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</div>}
            <input
                type={type}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                inputMode={inputMode}
                maxLength={maxLength}
                className={`mt-1 w-full rounded-lg border py-2 pr-4 ${icon ? "pl-9" : "pl-4"
                    } focus:ring-2 ${error ? "border-red-300 focus:ring-red-200" : "focus:ring-green-300"
                    }`}
            />
        </div>
        {error ? (
            <p className="mt-1 text-xs text-red-500">{error}</p>
        ) : hint ? (
            <p className="mt-1 text-xs text-gray-500">{hint}</p>
        ) : null}
    </div>
)

type PasswordInputProps = {
    label: string
    value: string
    setValue: (value: string) => void
    show: boolean
    setShow: (value: boolean) => void
    error?: string
    hint?: string
}

const PasswordInput = ({
    label,
    value,
    setValue,
    show,
    setShow,
    error,
    hint,
}: PasswordInputProps) => (
    <div>
        <label className="text-sm font-medium text-gray-600">{label}</label>
        <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                className={`mt-1 w-full rounded-lg border py-2 pl-9 pr-10 focus:ring-2 ${error ? "border-red-300 focus:ring-red-200" : "focus:ring-green-300"
                    }`}
            />
            <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
        </div>
        {error ? (
            <p className="mt-1 text-xs text-red-500">{error}</p>
        ) : hint ? (
            <p className="mt-1 text-xs text-gray-500">{hint}</p>
        ) : null}
    </div>
)

export default Register
